import React, { useCallback, useEffect, useState } from 'react';

import { Box, Button, Loader, ModalBox, Stack, Typography } from 'components';
import { useIsMobile, useNotifications } from 'hooks';
import { useUserRightsIn } from 'hooks/useUserRightsIn';

import { PaymentHistory } from './PaymentHistory';
import { PlanPicker } from './PlanPicker';
import { SubscriptionPanel } from './SubscriptionPanel';
import { TopUpPanel } from './TopUpPanel';
import { UsagePanel } from './UsagePanel';

import { useSearchParams, useSelector, useTranslation } from 'third-party';

import { BILLING_STATUS_PARAM } from 'constants/common';

import { useBilling } from 'modules/billing';

import { billingSelectors } from 'selectors';

import { getServerErrorMessage } from 'utils/helper';

import { APIUserRight } from 'types/api';
import { BillingModal } from 'types/app';

export const UsageBillingTab = () => {
  const { t } = useTranslation();
  const notifications = useNotifications();
  const hasRight = useUserRightsIn();
  const [searchParams, setSearchParams] = useSearchParams();
  const [openModal, setOpenModal] = useState<BillingModal | null>(null);
  const isMobile = useIsMobile();

  const {
    cancelSubscription,
    clearBilling,
    createTopUpCheckout,
    loadBillingOverview,
    loadEntitlement,
    loadPackQuote,
    loadInvoices,
    loadPurchases,
    openBillingPortal,
    subscribeToPlan,
  } = useBilling();

  const overview = useSelector(billingSelectors.overview);
  const usage = useSelector(billingSelectors.usage);
  const plan = useSelector(billingSelectors.currentPlan);
  const plans = useSelector(billingSelectors.plans);
  const packTiers = useSelector(billingSelectors.packTiers);
  const subscription = useSelector(billingSelectors.subscription);
  const bankAccountCount = useSelector(billingSelectors.bankAccountCount);
  const isStripeConfigured = useSelector(billingSelectors.isStripeConfigured);
  const quote = useSelector(billingSelectors.quote);
  const purchases = useSelector(billingSelectors.purchases);
  const invoices = useSelector(billingSelectors.invoices);

  const isOverviewPending = useSelector(
    billingSelectors.loadBillingOverviewPending,
  );
  const isQuotePending = useSelector(billingSelectors.loadPackQuotePending);
  const isCheckoutPending = useSelector(
    billingSelectors.createTopUpCheckoutPending,
  );
  const isSubscribePending = useSelector(
    billingSelectors.subscribeToPlanPending,
  );
  const isPortalPending = useSelector(
    billingSelectors.openBillingPortalPending,
  );
  const isCancelPending = useSelector(
    billingSelectors.cancelSubscriptionPending,
  );

  const canManageBilling = hasRight(APIUserRight.ManageOrganization);

  useEffect(() => {
    loadBillingOverview();
    loadPurchases();
    loadInvoices();

    return () => {
      clearBilling();
    };
  }, [loadBillingOverview, loadPurchases, loadInvoices, clearBilling]);

  // Stripe redirects back with the outcome in the query string; the balance itself is moved by the
  // webhook, so this only reports and refreshes.
  useEffect(() => {
    const status = searchParams.get(BILLING_STATUS_PARAM);
    if (!status) return;

    if (status === 'canceled') {
      notifications.error(t('billing.checkout.canceled'));
    } else {
      notifications.success(t('billing.checkout.completed'));
      loadBillingOverview();
      loadPurchases();
      loadInvoices();
      loadEntitlement();
    }

    searchParams.delete(BILLING_STATUS_PARAM);
    setSearchParams(searchParams, { replace: true });
  }, [
    searchParams,
    setSearchParams,
    notifications,
    t,
    loadBillingOverview,
    loadPurchases,
    loadInvoices,
    loadEntitlement,
  ]);

  const onTopUp = useCallback(
    async (quantity: number) => {
      const result: any = await createTopUpCheckout(quantity);
      const url = result?.payload?.url;

      if (url) {
        window.location.assign(url);
        return;
      }

      notifications.error(
        getServerErrorMessage(result, t('billing.checkout.failed')),
      );
    },
    [createTopUpCheckout, notifications, t],
  );

  const onSelectPlan = useCallback(
    async (planCode: string) => {
      const result: any = await subscribeToPlan(planCode);
      const url = result?.payload?.url;

      if (url) {
        window.location.assign(url);
        return;
      }

      if (result?.error) {
        notifications.error(
          getServerErrorMessage(result, t('billing.checkout.failed')),
        );
        return;
      }

      // A free plan needs no payment, so it is already in force and there is nothing left to pick.
      notifications.success(t('billing.plan.changed'));
      setOpenModal(null);
      loadBillingOverview();
      // The plan gates what the app offers, so the entitlement has to be re-read with it.
      loadEntitlement();
    },
    [subscribeToPlan, loadBillingOverview, loadEntitlement, notifications, t],
  );

  const onOpenPortal = useCallback(async () => {
    const result: any = await openBillingPortal();
    const url = result?.payload?.url;

    if (url) {
      window.location.assign(url);
      return;
    }

    notifications.error(
      getServerErrorMessage(result, t('billing.checkout.failed')),
    );
  }, [openBillingPortal, notifications, t]);

  const onCancel = useCallback(async () => {
    const result: any = await cancelSubscription();

    if (result?.error) {
      notifications.error(
        getServerErrorMessage(result, t('billing.subscription.cancelFailed')),
      );
      return;
    }

    notifications.success(t('billing.subscription.canceled'));
    loadBillingOverview();
  }, [cancelSubscription, loadBillingOverview, notifications, t]);

  if (isOverviewPending && !overview) {
    return <Loader />;
  }

  if (!overview || !usage) {
    return (
      <Box p={2}>
        <Typography variant="body2" color="text.secondary">
          {t('billing.loadFailed')}
        </Typography>
      </Box>
    );
  }

  return (
    <Stack spacing={3} sx={{ maxWidth: '64rem' }}>
      <Typography variant="h2">{t('billing.header')}</Typography>

      <UsagePanel
        usage={usage}
        plan={plan}
        bankAccountCount={bankAccountCount}
      />

      {canManageBilling ? (
        <Stack direction="row" flexWrap="wrap" spacing={1.5} useFlexGap>
          <Button
            variant="contained"
            size="small"
            onClick={() => setOpenModal(BillingModal.Plans)}
            sx={{
              color: 'primary.contrastText',
              '&.Mui-disabled': { color: 'primary.contrastText' },
            }}
          >
            {t('billing.plan.change')}
          </Button>
          <Button
            variant="outlined"
            size="small"
            onClick={() => setOpenModal(BillingModal.TopUp)}
          >
            {t('billing.topUp.open')}
          </Button>
        </Stack>
      ) : (
        <Typography variant="body2" color="text.secondary">
          {t('billing.needsManageRight')}
        </Typography>
      )}

      <SubscriptionPanel
        plan={plan}
        subscription={subscription}
        isPortalPending={isPortalPending}
        isCancelPending={isCancelPending}
        onOpenPortal={onOpenPortal}
        onCancel={onCancel}
      />

      <PaymentHistory purchases={purchases} invoices={invoices} />

      {openModal === BillingModal.Plans && (
        <ModalBox
          width={isMobile ? '92%' : '70rem'}
          height={isMobile ? '85vh' : 'min(46rem, 85vh)'}
          onClose={() => setOpenModal(null)}
        >
          <Box
            sx={{
              position: 'sticky',
              top: 0,
              zIndex: 1,
              backgroundColor: 'background.paper',
              borderBottom: '1px solid',
              borderColor: 'divider',
              pl: 3,
              pr: 6,
              pt: 3,
              pb: 1.5,
            }}
          >
            <Typography variant="h2">{t('billing.plan.header')}</Typography>
            {usage.isUnlimited && (
              <Typography variant="body2" color="text.secondary" mt={0.5}>
                {t('billing.plan.unlimitedLocked')}
              </Typography>
            )}
          </Box>

          <Box sx={{ p: 3 }}>
            <PlanPicker
              plans={plans}
              currentPlanCode={plan?.code ?? null}
              isStripeConfigured={isStripeConfigured}
              isPending={isSubscribePending}
              isUnlimited={usage.isUnlimited}
              onSelect={onSelectPlan}
            />
          </Box>
        </ModalBox>
      )}

      {openModal === BillingModal.TopUp && (
        <ModalBox
          width={isMobile ? '90%' : '44rem'}
          height={isMobile ? '85vh' : 'min(34rem, 85vh)'}
          onClose={() => setOpenModal(null)}
        >
          <Box p={3}>
            <TopUpPanel
              tiers={packTiers}
              quote={quote}
              isQuotePending={isQuotePending}
              isCheckoutPending={isCheckoutPending}
              isStripeConfigured={isStripeConfigured}
              onQuantityChange={loadPackQuote}
              onCheckout={onTopUp}
            />
          </Box>
        </ModalBox>
      )}
    </Stack>
  );
};
